import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';

type BeneficiarySelectFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  form: UseFormReturn<TFieldValues>;
  label: string;
  badge?: string;
  inputName: TName;
  placeholder?: string;
  onChange?: (value: TFieldValues[TName]) => void;
  className?: string;
  children: React.ReactNode;
};

export const BeneficiarySelectField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  form,
  label,
  badge,
  inputName,
  placeholder,
  onChange,
  className,
  children,
}: BeneficiarySelectFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={form.control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <FormItem onBlur={field.onBlur}>
            <Stack space={1} align="stretch" className="relative">
              {badge && <p className="absolute right-3 top-1 text-[10px] font-medium text-seapoint-gray">{badge}</p>}

              <FormControl>
                <Select
                  aria-label={label}
                  required
                  placeholder={placeholder}
                  value={field.value}
                  onValueChange={(value: TFieldValues[TName]) => {
                    field.onChange(value);

                    onChange?.(value);
                  }}
                  triggerLabel={<FormLabel className="text-xs font-medium text-seapoint-gray">{label}</FormLabel>}
                  triggerClassName={cn(
                    'px-4 py-3 rounded-lg [&_.account-logo]:w-4 [&_.account-logo]:h-4 [&_.account-type]:hidden bg-white border border-seapoint-gray-border text-black text-base font-medium',
                    { 'border-seapoint-red': !!formState.errors.country },
                    className,
                  )}
                >
                  {children}
                </Select>
              </FormControl>

              <FormMessage className="w-full text-right" />
            </Stack>
          </FormItem>
        );
      }}
    />
  );
};
